import { errorToJson, useGetRequest } from '$shared/utils';
import { useNotification } from '~/shared/hooks/useNotification';
import { useStorePaths } from './useStorePaths';
import { StoreSearchOverviewModel, StoreStoresDetailParams } from '~/lib/data-contract';

export type StoresSearchParams = Pick<
    StoreStoresDetailParams,
    'Latitude' | 'Longitude' | 'ZipCode' | 'City' | 'Radius'
>;

const defaultSearchParams: StoresSearchParams = {
    Latitude: undefined,
    Longitude: undefined,
    ZipCode: undefined,
    City: undefined,
    Radius: 50,
};

export type UseStoresProps = {
    /**
     * Parameters that the search endpoint accepts
     */
    searchParams?: StoresSearchParams;
    /**
     * SearchTerm that will work for either city or zip. Will overwrite searchParams if both are provided
     */
    searchTerm?: string;
};

const isNumberRegex = /^\d+$/;

export const useStores = ({ searchParams, searchTerm }: UseStoresProps) => {
    const { storesPath } = useStorePaths();
    const { push } = useNotification();

    const params = {
        ...defaultSearchParams,
        ...searchParams,
        ...(searchTerm && { [isNumberRegex.test(searchTerm) ? 'ZipCode' : 'City']: searchTerm }),
    };

    const { Latitude, Longitude, City, ZipCode } = params;

    const shouldSearch = Boolean((Latitude && Longitude) || City || ZipCode);

    const { data: stores, error, isLoading, isFetching, isError, isIdle, status } = useGetRequest<
        StoreSearchOverviewModel,
        Response
    >(storesPath, {
        params,
        keepPreviousData: shouldSearch,
        enabled: shouldSearch,
        retry: 1,
        onError: async (error: Response) => {
            const { level, errorMessages } = await errorToJson(error);
            push({ severity: level, text: errorMessages });
        },
    });

    return {
        data: stores,
        status,
        error,
        isLoading,
        isFetching,
        isError,
        isIdle,
        params,
    };
};
